import type { SubscriptionTier, BillingInterval } from '@/integrations/supabase/types';

export type { BillingInterval };

export interface PlanPricing {
  price: number;
  stripePriceId: string | null;
}

export interface Plan {
  name: string;
  nameEn: string;
  products: number | 'unlimited';
  lots: number | 'unlimited';
  feedbackPerMonth: number | 'unlimited';
  hasAI: boolean;
  pricing: {
    monthly: PlanPricing;
    yearly: PlanPricing;
  };
}

export const PLANS: Record<SubscriptionTier, Plan> = {
  free: {
    name: 'Gratuito',
    nameEn: 'Free',
    products: 1,
    lots: 2,
    feedbackPerMonth: 50,
    hasAI: false,
    pricing: {
      monthly: { price: 0, stripePriceId: null },
      yearly: { price: 0, stripePriceId: null },
    },
  },
  starter: {
    name: 'Starter',
    nameEn: 'Starter',
    products: 5,
    lots: 15,
    feedbackPerMonth: 500,
    hasAI: true,
    pricing: {
      monthly: { 
        price: 99, 
        stripePriceId: import.meta.env.VITE_STRIPE_STARTER_MONTHLY_PRICE_ID || null 
      },
      yearly: { 
        price: 1010, 
        stripePriceId: import.meta.env.VITE_STRIPE_STARTER_YEARLY_PRICE_ID || null 
      },
    },
  },
  pro: {
    name: 'Pro',
    nameEn: 'Pro',
    products: 'unlimited',
    lots: 'unlimited',
    feedbackPerMonth: 2000,
    hasAI: true,
    pricing: {
      monthly: { 
        price: 259, 
        stripePriceId: import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID || null 
      },
      yearly: { 
        price: 2640, 
        stripePriceId: import.meta.env.VITE_STRIPE_PRO_YEARLY_PRICE_ID || null 
      },
    },
  },
} as const;

export const YEARLY_DISCOUNT_PERCENT = 15;

export const PLAN_ORDER: SubscriptionTier[] = ['free', 'starter', 'pro'];

/**
 * Get yearly savings amount for a given tier
 */
export function getYearlySavings(tier: SubscriptionTier): number {
  const plan = PLANS[tier];
  if (tier === 'free') return 0;
  
  const monthlyPrice = plan.pricing.monthly.price;
  const yearlyPrice = plan.pricing.yearly.price;
  const monthlyTotal = monthlyPrice * 12;
  
  return monthlyTotal - yearlyPrice;
}

/**
 * Get effective monthly price when paying yearly
 */
export function getEffectiveMonthlyPrice(tier: SubscriptionTier): number {
  const plan = PLANS[tier];
  if (tier === 'free') return 0;
  
  return Math.round(plan.pricing.yearly.price / 12);
}

/**
 * Get product limit for a tier (returns Infinity for unlimited)
 */
export function getProductLimit(tier: SubscriptionTier): number {
  const limit = PLANS[tier].products;
  return limit === 'unlimited' ? Infinity : limit;
}

/**
 * Get feedback limit for a tier (returns Infinity for unlimited)
 */
export function getFeedbackLimit(tier: SubscriptionTier): number {
  const limit = PLANS[tier].feedbackPerMonth;
  return limit === 'unlimited' ? Infinity : limit;
}

/**
 * Get lot limit for a tier (returns Infinity for unlimited)
 */
export function getLotLimit(tier: SubscriptionTier): number {
  const limit = PLANS[tier].lots;
  return limit === 'unlimited' ? Infinity : limit;
}

/**
 * Check if a tier has AI features enabled
 */
export function hasAiAccess(tier: SubscriptionTier): boolean {
  return PLANS[tier].hasAI;
}

/**
 * Check if a tier can be upgraded to another tier
 */
export function canUpgradeTo(currentTier: SubscriptionTier, targetTier: SubscriptionTier): boolean {
  const currentIndex = PLAN_ORDER.indexOf(currentTier);
  const targetIndex = PLAN_ORDER.indexOf(targetTier);
  return targetIndex > currentIndex;
}

/**
 * Check if a tier can be downgraded to another tier
 * Includes free tier - redirects to Stripe Portal for cancellation
 */
export function canDowngradeTo(currentTier: SubscriptionTier, targetTier: SubscriptionTier): boolean {
  const currentIndex = PLAN_ORDER.indexOf(currentTier);
  const targetIndex = PLAN_ORDER.indexOf(targetTier);
  return targetIndex < currentIndex && targetIndex >= 0;
}

/**
 * Get the next tier for upgrade (returns null if already on pro)
 */
export function getNextTier(currentTier: SubscriptionTier): SubscriptionTier | null {
  const currentIndex = PLAN_ORDER.indexOf(currentTier);
  if (currentIndex >= PLAN_ORDER.length - 1) return null;
  return PLAN_ORDER[currentIndex + 1];
}

/**
 * Format price in BRL
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Get display info for a tier (for UI badges, etc.)
 */
export function getTierDisplayInfo(tier: SubscriptionTier): {
  label: string;
  color: string;
  bgColor: string;
} {
  const displayInfo: Record<SubscriptionTier, { label: string; color: string; bgColor: string }> = {
    free: { label: 'Gratuito', color: 'text-stone-700', bgColor: 'bg-stone-100' },
    starter: { label: 'Starter', color: 'text-amber-700', bgColor: 'bg-amber-100' },
    pro: { label: 'Pro', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  };
  
  return displayInfo[tier];
}
